/**
 * Vendor Store
 * Zustand store for vendor state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { VendorListItem, VendorDetailResponse } from '@/types/api/vendor';

/**
 * Vendor filter state
 */
export interface VendorFilters {
  search: string;
  categoryIds: number[];
  country: string; // Format: "CountryID-StateID-CityID,CountryID-StateID,CountryID"
  isActive: boolean | null;
}

/**
 * Vendor pagination state
 */
export interface VendorPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Vendor sort state
 */
export interface VendorSort {
  sortBy: number | null;
  sortOrder: 0 | 1; // 0=ASC, 1=DESC
}

/**
 * Vendor store state
 */
export interface VendorState {
  // Data
  vendors: VendorListItem[];
  currentVendor: VendorDetailResponse | null;

  // Pagination
  pagination: VendorPagination;

  // Filters
  filters: VendorFilters;

  // Sorting
  sort: VendorSort;

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions - Data
  setVendors: (vendors: VendorListItem[]) => void;
  setCurrentVendor: (vendor: VendorDetailResponse | null) => void;
  addVendor: (vendor: VendorListItem) => void;
  updateVendor: (vendorId: number, vendor: VendorDetailResponse) => void;
  updateVendorInList: (vendorId: number, updates: Partial<VendorListItem>) => void;
  removeVendor: (vendorId: number) => void;

  // Actions - Pagination
  setPagination: (pagination: Partial<VendorPagination>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;

  // Actions - Filters
  setFilters: (filters: Partial<VendorFilters>) => void;
  setSearch: (search: string) => void;
  setCategoryIds: (categoryIds: number[]) => void;
  setCountry: (country: string) => void;
  setIsActive: (isActive: boolean | null) => void;
  resetFilters: () => void;

  // Actions - Sorting
  setSort: (sort: Partial<VendorSort>) => void;
  setSortBy: (sortBy: number | null) => void;
  setSortOrder: (sortOrder: 0 | 1) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  vendors: [],
  currentVendor: null,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    categoryIds: [],
    country: '',
    isActive: null,
  },
  sort: {
    sortBy: null,
    sortOrder: 0 as 0 | 1,
  },
  isLoading: false,
  error: null,
};

/**
 * Vendor Zustand store
 */
export const useVendorStore = create<VendorState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Data actions
        setVendors: (vendors) =>
          set({ vendors }, false, 'vendors/setVendors'),

        setCurrentVendor: (vendor) =>
          set({ currentVendor: vendor }, false, 'vendors/setCurrentVendor'),

        addVendor: (vendor) =>
          set(
            (state) => ({ vendors: [vendor, ...state.vendors] }),
            false,
            'vendors/addVendor'
          ),

        updateVendor: (vendorId, vendor) =>
          set(
            (state) => ({
              vendors: state.vendors.map((v) =>
                v.vendorId === vendorId
                  ? {
                      ...v,
                      name: vendor.name,
                      alternativeName: vendor.alternativeName,
                      nickname: vendor.nickname,
                      vendorCode: vendor.vendorCode,
                      isActive: vendor.isActive,
                    }
                  : v
              ),
              currentVendor: state.currentVendor?.vendorId === vendorId ? vendor : state.currentVendor,
            }),
            false,
            'vendors/updateVendor'
          ),

        updateVendorInList: (vendorId, updates) =>
          set(
            (state) => ({
              vendors: state.vendors.map((v) =>
                v.vendorId === vendorId ? { ...v, ...updates } : v
              ),
            }),
            false,
            'vendors/updateVendorInList'
          ),

        removeVendor: (vendorId) =>
          set(
            (state) => ({
              vendors: state.vendors.filter((v) => v.vendorId !== vendorId),
            }),
            false,
            'vendors/removeVendor'
          ),

        // Pagination actions
        setPagination: (pagination) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, ...pagination },
            }),
            false,
            'vendors/setPagination'
          ),

        setPage: (page) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, page },
            }),
            false,
            'vendors/setPage'
          ),

        setPageSize: (pageSize) =>
          set(
            (state) => ({
              pagination: { ...state.pagination, pageSize, page: 1 },
            }),
            false,
            'vendors/setPageSize'
          ),

        // Filter actions
        setFilters: (filters) =>
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
              pagination: { ...state.pagination, page: 1 }, // Reset to page 1
            }),
            false,
            'vendors/setFilters'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'vendors/setSearch'
          ),

        setCategoryIds: (categoryIds) =>
          set(
            (state) => ({
              filters: { ...state.filters, categoryIds },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'vendors/setCategoryIds'
          ),

        setCountry: (country) =>
          set(
            (state) => ({
              filters: { ...state.filters, country },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'vendors/setCountry'
          ),

        setIsActive: (isActive) =>
          set(
            (state) => ({
              filters: { ...state.filters, isActive },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'vendors/setIsActive'
          ),

        resetFilters: () =>
          set(
            {
              filters: initialState.filters,
              pagination: { ...initialState.pagination },
            },
            false,
            'vendors/resetFilters'
          ),

        // Sort actions
        setSort: (sort) =>
          set(
            (state) => ({
              sort: { ...state.sort, ...sort },
            }),
            false,
            'vendors/setSort'
          ),

        setSortBy: (sortBy) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortBy },
            }),
            false,
            'vendors/setSortBy'
          ),

        setSortOrder: (sortOrder) =>
          set(
            (state) => ({
              sort: { ...state.sort, sortOrder },
            }),
            false,
            'vendors/setSortOrder'
          ),

        // UI state actions
        setLoading: (isLoading) =>
          set({ isLoading }, false, 'vendors/setLoading'),

        setError: (error) =>
          set({ error }, false, 'vendors/setError'),

        // Reset action
        reset: () =>
          set(initialState, false, 'vendors/reset'),
      }),
      {
        name: 'vendor-storage',
        // Only persist filters, pagination settings, and sort
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
          sort: state.sort,
        }),
        // Merge persisted state with initial state, ensuring isActive defaults to true
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<VendorState> | undefined;
          return {
            ...currentState,
            ...persisted,
            filters: {
              ...currentState.filters,
              ...persisted?.filters,
              // Always default isActive to true if it was null/undefined in storage
              isActive: persisted?.filters?.isActive ?? true,
            },
          };
        },
      }
    ),
    { name: 'VendorStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get filter query parameters for API
export const useVendorQueryParams = () => {
  const { filters, pagination, sort } = useVendorStore();

  // Build params object, only including defined values
  const params: Record<string, string | number | boolean | undefined> = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortOrder: sort.sortOrder,
  };

  // Add optional filters only if they have values
  if (filters.search && filters.search.trim()) {
    params.search = filters.search.trim();
  }

  if (filters.categoryIds && filters.categoryIds.length > 0) {
    params.categoryIds = filters.categoryIds.join(',');
  }

  if (filters.country && filters.country.trim()) {
    params.country = filters.country.trim();
  }

  // Convert isActive boolean to API format: 1=active, 2=inactive, 3=all
  if (filters.isActive === true) {
    params.isActive = 1;
  } else if (filters.isActive === false) {
    params.isActive = 2;
  } else {
    params.isActive = 3;
  }

  if (sort.sortBy !== null) {
    params.sortBy = sort.sortBy;
  }

  return params;
};

// Check if filters are applied
export const useHasActiveFilters = () => {
  const { filters } = useVendorStore();

  return (
    filters.search !== '' ||
    filters.categoryIds.length > 0 ||
    filters.country !== '' ||
    filters.isActive !== null
  );
};
